import { useMemo } from 'react';
import { useTokenProvider, useConfig, useAuthorizer, useDealerInfo } from '@gplibraries/react/hooks';
import { useQuery } from '@tanstack/react-query';
import { fetchDealerList, fetchAssociatedDealers, fetchTmAssociatedDealers } from '@gplibraries/react/api';
/**
 * Fetch and compute the list of dealers that the current user can switch to, based on their roles and current mask.
 *
 * @returns {FetchResult}
 */
export function useAssociatedDealers() {
    const { dealerAccessApiScope, dealerAccessApiHost } = useConfig();
    const { data: dealerInfo, loading: dealerInfoLoading, error: dealerInfoError } = useDealerInfo();
    const getToken = useTokenProvider({ scopes: [dealerAccessApiScope] });
    // middleware token query
    const { data: token, isLoading: tokenLoading } = useQuery({
        enabled: Boolean(getToken),
        queryKey: ['middlewareToken'],
        queryFn: () => getToken()
    });
    const user = useAuthorizer(token?.accessTokenClaims);
    // dealer list request
    const { data: dealerList, error: dealersError, isLoading: dealersLoading } = useQuery({
        enabled: Boolean(getToken),
        queryKey: ['dealerList'],
        queryFn: () => getToken()
            .then(token => fetchDealerList(dealerAccessApiHost, token.accessToken))
            .then(res => res.json())
    });
    // associated dealers request
    const { data: associatedDealers, isLoading: associatedDealersLoading, error: associatedDealersError } = useQuery({
        enabled: Boolean(user !== null && (user?.isTm() || user?.isDealerEmployeeUser())),
        queryKey: ['associatedDealers'],
        queryFn: (user?.isTm() && user?.get('id'))
            ? () => getToken()
                .then(token => fetchTmAssociatedDealers(dealerAccessApiHost, token.accessToken))
                .then(res => res.json())
            : () => getToken()
                .then(token => fetchAssociatedDealers(dealerAccessApiHost, token.accessToken, user.get('id')))
                .then(res => res.json())
    });
    const data = useMemo(() => {
        if (user && dealerList) {
            // If a user is an admin or CS user, they can switch to any dealer.
            if ((user.isAdmin() || user.isCsUser()) && dealerList) {
                return dealerList?.data.filter((dlr) => dlr.dealer_number !== dealerInfo?.dealer_number);
            }
            // If this is a dealer account and they are the parent dealer, they can switch to any of their sister stores
            // if (user.isDealer() && dealerInfo && (user.get('gpmUsername') === dealerInfo.parent_dealer)) {
            //   return dealerList?.data.filter((dlr: DynamicObject) => dealerInfo.sister_stores.includes(dlr.dealer_number));
            // }
            // Dealers cannot morph at this time, may implement the above logic at a later date.
            if (user.isDealer()) {
                return [];
            }
            // For the remaining users, get their associated dealers from the API. For dealer employee users this is their sister
            // stores for which they have permissions, and for TMs this is the dealers in their territory.
            if (associatedDealers && associatedDealers?.data?.length > 0 && dealerList?.data) {
                return dealerList.data.filter((dlr) => associatedDealers.data.includes(dlr.dealer_number) &&
                    dlr.dealer_number !== dealerInfo?.dealer_number);
            }
        }
        return [];
    }, [user, dealerList, associatedDealers, dealerInfo]);
    const loading = tokenLoading || dealerInfoLoading || dealersLoading || associatedDealersLoading;
    const error = dealerInfoError || dealersError || associatedDealersError;
    return { data, loading, error };
}
